import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionUser } from '@auctionradar/shared';
import { api, ApiCallError } from './client';

/**
 * Session state machine:
 *   - `loading`: bootstrap call to /api/auth/me hasn't returned yet.
 *   - `authed` : valid cookie, user populated.
 *   - `anon`   : 401 from /me OR explicit logout. Login page should render.
 */
export type SessionState =
  | { status: 'loading' }
  | { status: 'authed'; user: SessionUser }
  | { status: 'anon' };

type SessionContextValue = {
  state: SessionState;
  login: (inviteCode: string) => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Wraps the app and bootstraps the session by calling /api/auth/me once.
 * The cookie itself is HttpOnly so we can't inspect it from JS — we just
 * ask the API "do I have a valid session?" on mount.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then(({ user }) => {
        if (!cancelled) setState({ status: 'authed', user });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiCallError && err.status === 401) {
          setState({ status: 'anon' });
        } else {
          // Unexpected (network, 5xx, schema). Treat as anon so the user can
          // still try to log in — we don't want a transient outage to wedge
          // the app.
          setState({ status: 'anon' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      login: async (inviteCode: string) => {
        const { user } = await api.login({ inviteCode });
        setState({ status: 'authed', user });
      },
      logout: async () => {
        await api.logout().catch(() => undefined);
        setState({ status: 'anon' });
      },
    }),
    [state],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
